import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Typography, Row, Col, Statistic, Select, Input, message, Modal, Badge, Alert, List, Avatar } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, CalendarOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { formatCurrency, getStatusColor } from '../../../utils/ui';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

function QuotesList() {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [listError, setListError] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });
    const [apiUnavailable, setApiUnavailable] = useState(false);
    const [respondingId, setRespondingId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        fetchQuotes();
        fetchStats();
    }, [filters]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    const fetchQuotes = async () => {
        try {
            setLoading(true);
            setListError('');
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            
            console.log('Fetching quotes with params:', params.toString());
            const response = await api.get(`/quotes?${params.toString()}`);
            console.log('Quotes API response:', response);
            
            let list = Array.isArray(response?.data?.data)
                ? response.data.data
                : (Array.isArray(response?.data) ? response.data : (response?.data?.data?.data || []));
            // Support singleton object responses
            if (!Array.isArray(list)) {
                if (response?.data?.id) list = [response.data];
                else if (response?.id) list = [response];
                else list = [];
            }
            console.log('Processed quotes list:', list);
            setQuotes(list);
        } catch (error) {
            message.error('Failed to fetch quotes');
            console.error('Quotes fetch error:', error);
            console.error('Error response:', error.response?.data);
            setListError('Quotes service is temporarily unavailable');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            let response;
            for (let attempt = 1; attempt <= 2; attempt++) {
                try {
                    response = await api.get('/quotes/stats');
                    break;
                } catch (_) {
                    if (attempt === 2) {
                        response = { data: { data: {} } };
                    } else {
                        await delay(300 * attempt);
                    }
                }
            }
            setStats(response.data.data || {});
            setApiUnavailable(false);
        } catch (error) {
            console.error('Stats fetch error:', error);
            setApiUnavailable(true);
        }
    };

    const handleViewQuote = (quote) => {
        navigate(`/quotes/${quote.id}`);
    };

    const handleQuoteResponse = (status, updatedQuote) => {
        // Update the quote in the list
        setQuotes(prev => prev.map(q => q.id === updatedQuote.id ? updatedQuote : q));
        fetchStats(); // Refresh stats
    };

    const acceptQuote = async (quote) => {
        // Optimistic UI: update immediately
        const prev = quotes;
        const optimistic = quotes.map(q => q.id === quote.id ? { ...q, status: 'accepted', responded_at: new Date().toISOString() } : q);
        setQuotes(optimistic);
        setRespondingId(quote.id);
        message.loading({ content: 'Accepting quote...', key: `accept-${quote.id}` });

        try {
            const res = await api.acceptQuote(quote.id, '');
            const updated = res?.data?.data || res?.data || quote;
            handleQuoteResponse('accepted', updated);
            message.success({ content: 'Quote accepted', key: `accept-${quote.id}`, duration: 2 });
        } catch (e) {
            // Revert on failure
            setQuotes(prev);
            message.error({ content: 'Failed to accept quote', key: `accept-${quote.id}` });
            console.error('Accept quote error:', e);
        } finally {
            setRespondingId(null);
        }
    };

    const rejectQuote = async (quote) => {
        Modal.confirm({
            title: 'Reject Quote',
            content: 'Are you sure you want to reject this quote?',
            onOk: async () => {
                try {
                    setRespondingId(quote.id);
                    const res = await api.rejectQuote(quote.id, '');
                    if (res.success) {
                        handleQuoteResponse('rejected', res.data.data || res.data);
                        message.success('Quote rejected');
                    } else {
                        message.error(res.message || 'Failed to reject quote');
                    }
                } catch (e) {
                    message.error('Failed to reject quote');
                    console.error('Reject quote error:', e);
                } finally {
                    setRespondingId(null);
                }
            }
        });
    };

    const statusColor = (status) => getStatusColor(status);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <ClockCircleOutlined />;
            case 'quoted': return <FileTextOutlined />;
            case 'accepted': return <CheckCircleOutlined />;
            case 'rejected': return <CloseCircleOutlined />;
            case 'expired': return <ExclamationCircleOutlined />;
            default: return <ClockCircleOutlined />;
        }
    };

    const columns = [
        {
            title: 'Quote #',
            dataIndex: 'quote_number',
            key: 'quote_number',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Vendor',
            dataIndex: ['vendor', 'name'],
            key: 'vendor',
            render: (text, record) => (record?.vendor?.name || record?.vendor?.business_name || 'Unknown Vendor')
        },
        {
            title: 'Event Date',
            dataIndex: ['event_details', 'date'],
            key: 'event_date',
            render: (_, record) => {
                const date = record?.event_details?.date || record?.event_date;
                return date ? dayjs(date).format('MMM DD, YYYY') : '-';
            }
        },
        {
            title: 'Occasion',
            dataIndex: ['event_details', 'occasion'],
            key: 'occasion',
            render: (text) => text || '-'
        },
        {
            title: 'Guests',
            dataIndex: ['event_details', 'guest_count'],
            key: 'guests',
            render: (_, record) => {
                const count = record?.event_details?.guest_count ?? record?.guest_count;
                return count ? `${count} people` : '-';
            }
        },
        {
            title: 'Total Amount',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount) => (
                <Text strong className="text-orange-600">
                    {formatCurrency(amount ?? 0)}
                </Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColor(status)} icon={getStatusIcon(status)}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Valid Until',
            dataIndex: 'valid_until',
            key: 'valid_until',
            render: (date) => {
                if (!date) return '-';
                const isExpired = dayjs(date).isBefore(dayjs());
                return (
                    <Text type={isExpired ? 'danger' : 'secondary'}>
                        {dayjs(date).format('MMM DD, YYYY')}
                    </Text>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space wrap>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewQuote(record)}
                        size="small"
                    >
                        View
                    </Button>
                    {(record.status === 'pending' || record.status === 'quoted') && (
                        <>
                            <Button
                                type="primary"
                                size="small"
                                loading={respondingId === record.id}
                                onClick={() => acceptQuote(record)}
                            >
                                Accept
                            </Button>
                            <Button
                                danger
                                size="small"
                                loading={respondingId === record.id}
                                onClick={() => rejectQuote(record)}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {listError && (
                <Alert
                    type="warning"
                    message={listError}
                    showIcon
                    action={<Button size="small" onClick={fetchQuotes}>Retry</Button>}
                />
            )}
            {apiUnavailable && (
                <Alert
                    type="warning"
                    message="Quotes service is temporarily unavailable"
                    description="You can still view existing quotes, but counts and updates may be delayed."
                    showIcon
                    action={
                        <Button size="small" onClick={() => { fetchQuotes(); fetchStats(); }}>Try again</Button>
                    }
                />
            )}
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <Title level={2} className="!mb-2">My Quotes</Title>
                    <Text type="secondary">
                        Review and manage your catering quotes
                    </Text>
                </div>
                <Button 
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        fetchQuotes();
                        fetchStats();
                    }}
                >
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Quotes"
                            value={stats.total || 0}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Pending"
                            value={stats.pending || 0}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Accepted"
                            value={stats.accepted || 0}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Rejected"
                            value={stats.rejected || 0}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8} md={6}>
                        <Text strong>Status:</Text>
                        <Select
                            placeholder="All Statuses"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            value={filters.status}
                            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                        >
                            <Option value="pending">Pending</Option>
                            <Option value="quoted">Quoted</Option>
                            <Option value="accepted">Accepted</Option>
                            <Option value="rejected">Rejected</Option>
                            <Option value="expired">Expired</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={8} md={12}>
                        <Text strong>Search:</Text>
                        <Search
                            placeholder="Search quotes..."
                            style={{ marginTop: 4 }}
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Quotes Table/List */}
            <Card>
                {isMobile ? (
                    // Mobile List View
                    <List
                        dataSource={quotes}
                        loading={loading}
                        renderItem={(quote) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="view"
                                        type="link"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleViewQuote(quote)}
                                        size="small"
                                    >
                                        View
                                    </Button>,
                                    ...(quote.status === 'pending' || quote.status === 'quoted' ? [
                                        <Button
                                            key="accept"
                                            type="primary"
                                            size="small"
                                            loading={respondingId === quote.id}
                                            onClick={() => acceptQuote(quote)}
                                        >
                                            Accept
                                        </Button>,
                                        <Button
                                            key="reject"
                                            danger
                                            size="small"
                                            loading={respondingId === quote.id}
                                            onClick={() => rejectQuote(quote)}
                                        >
                                            Reject
                                        </Button>
                                    ] : [])
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar 
                                            icon={getStatusIcon(quote.status)}
                                            style={{ 
                                                backgroundColor: getStatusColor(quote.status) === 'orange' ? '#faad14' : 
                                                                  getStatusColor(quote.status) === 'green' ? '#52c41a' : 
                                                                  getStatusColor(quote.status) === 'red' ? '#ff4d4f' : 
                                                                  getStatusColor(quote.status) === 'blue' ? '#1890ff' : '#d9d9d9'
                                            }}
                                        />
                                    }
                                    title={
                                        <div className="flex items-center gap-2">
                                            <Text strong>{quote.quote_number || `Quote #${quote.id}`}</Text>
                                            <Tag color={getStatusColor(quote.status)} size="small">
                                                {quote.status.toUpperCase()}
                                            </Tag>
                                        </div>
                                    }
                                    description={
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm">
                                                <UserOutlined />
                                                <Text type="secondary">
                                                    {quote.vendor?.name || quote.vendor?.business_name || 'Unknown Vendor'}
                                                </Text>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <CalendarOutlined />
                                                <Text type="secondary">
                                                    {quote.event_details?.date ? 
                                                        dayjs(quote.event_details.date).format('MMM DD, YYYY') : 
                                                        quote.event_date ? 
                                                        dayjs(quote.event_date).format('MMM DD, YYYY') : 
                                                        '-'
                                                    }
                                                </Text>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <DollarOutlined />
                                                <Text strong className="text-orange-600">
                                                    {formatCurrency(quote.total_amount ?? 0)}
                                                </Text>
                                            </div>
                                            {quote.event_details?.guest_count && (
                                                <div className="text-sm text-gray-500">
                                                    {quote.event_details.guest_count} guests
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: (total, range) => 
                                `${range[0]}-${range[1]} of ${total} quotes`
                        }}
                    />
                ) : (
                    // Desktop Table View
                    <Table
                        columns={columns}
                        dataSource={quotes}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => 
                                `${range[0]}-${range[1]} of ${total} quotes`
                        }}
                    />
                )}
            </Card>

        </div>
    );
}

export default QuotesList;
